import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, ShieldCheck, Copy, Share2, ExternalLink, Download } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { greetName, sharePdfToWhatsApp, renderMessage, signatureCompany } from '../../../lib/waShare';
import { formatErr, inputCls } from './shared';

const SERVICE_TYPES = [
  'Paint Protection Film',
  'Ceramic Coating',
  'Sunfilm / Window Tint',
  'Graphene Coating',
  'Glass Coating',
  'Headlight Protection',
  'Anti-Rust Underbody',
  'Detailing',
  'Other',
];

const todayISO = () => new Date().toISOString().slice(0, 10);

/**
 * Modal launched from the Invoice form. Lets admin create an eWarranty
 * pre-filled from the invoice's customer + vehicle. After save, shows the
 * generated warranty ID with one-click WhatsApp + Email handoff buttons.
 */
const InvoiceWarrantyModal = ({ invoice, customer, onClose, onCreated, apiBase }) => {
  const { authHeader } = useAuth();
  // Accept a per-instance API base so this modal can drive both non-GST
  // (`${API}/billing/invoices`) and GST (`${API}/gst/invoices`) warranty
  // creation endpoints.
  const base = apiBase || `${API}/billing/invoices`;
  const vehicles = customer?.vehicles || [];
  const defaultVehicleId = vehicles[0]?.id || '';

  const [form, setForm] = useState({
    service_type: 'Paint Protection Film',
    product_used: '',
    coverage_area: 'Full body',
    installer_name: 'Unisol Global',
    installer_location: 'Bangalore',
    installation_date: todayISO(),
    warranty_years: 5,
    lifetime_warranty: false,
    roll_id: '',
    vehicle_id: defaultVehicleId,
    auto_approve: true,
    send_email: !!customer?.email,
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null); // {warranty_id, status, warranty_expiry, email_sent}

  useEffect(() => {
    // If the customer prop loads after the modal mounts, sync the default vehicle.
    if (!form.vehicle_id && vehicles[0]?.id) {
      setForm((s) => ({ ...s, vehicle_id: vehicles[0].id }));
    }
  }, [vehicles, form.vehicle_id]);

  const selectedVehicle = vehicles.find((v) => v.id === form.vehicle_id) || vehicles[0];
  const canSave = !!form.service_type && !!selectedVehicle && !!selectedVehicle.registration_number;

  const submit = async (e) => {
    e?.preventDefault();
    setErr('');
    if (!canSave) {
      setErr('Customer needs a vehicle with a registration number before creating a warranty.');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${base}/${invoice.id}/warranty`,
        form,
        { headers: authHeader() },
      );
      setResult(data);
      onCreated && onCreated(data);
    } catch (e2) {
      setErr(formatErr(e2));
    } finally {
      setSaving(false);
    }
  };

  const shareWhatsApp = async () => {
    if (!result) return;
    const phone = (customer?.phone || '').replace(/\D/g, '');
    const veh = selectedVehicle || {};
    const message = renderMessage('warranty', {
      customer: greetName(customer?.prefix, customer?.name), company: signatureCompany() || 'workshop',
      warranty_id: result.warranty_id,
      vehicle: `${[veh.make, veh.model].filter(Boolean).join(' ')}${veh.registration_number ? ` (${veh.registration_number})` : ''}`,
      service: form.service_type || '', valid_till: result.lifetime_warranty ? 'Lifetime' : (result.warranty_expiry || ''),
      certificate_link: `${window.location.origin}/api/warranty/${result.warranty_id}/certificate`,
      verify_link: result.verify_url || '',
    });
    await sharePdfToWhatsApp({
      getPdf: async () => (await axios.get(`${API}/warranty/${result.warranty_id}/certificate`, { responseType: 'blob' })).data,
      filename: `eWarranty_${result.warranty_id}.pdf`, title: `eWarranty ${result.warranty_id}`,
      message, phone,
    });
  };

  const copyId = async () => {
    if (!result) return;
    try { await navigator.clipboard.writeText(result.warranty_id); alert('Warranty ID copied.'); }
    catch (e) {
      // Clipboard API is unavailable on insecure origins / some mobile browsers.
      // Fall back to a prompt so the user can still grab the ID manually.
      if (typeof console !== 'undefined' && console.debug) {
        console.debug('clipboard.writeText failed:', e);
      }
      window.prompt('Copy your Warranty ID:', result.warranty_id);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="invoice-warranty-modal"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-teal-600" />
            <div>
              <h2 className="text-base font-semibold text-slate-900">
                {result ? 'eWarranty Created' : 'Create eWarranty from Invoice'}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">Invoice {invoice.number}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100" data-testid="invoice-warranty-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5">
          {result ? (
            <SuccessView
              result={result}
              vehicle={selectedVehicle}
              customer={customer}
              serviceType={form.service_type}
              onShare={shareWhatsApp}
              onCopy={copyId}
              onClose={onClose}
            />
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {/* Vehicle picker */}
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Vehicle *</label>
                {vehicles.length === 0 ? (
                  <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                    This customer has no vehicles linked. Add one on the customer profile first.
                  </div>
                ) : (
                  <select
                    value={form.vehicle_id}
                    onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })}
                    className={inputCls}
                    data-testid="warranty-vehicle"
                  >
                    {vehicles.map((v) => (
                      <option key={v.id} value={v.id}>
                        {[v.make, v.model].filter(Boolean).join(' ')}
                        {v.registration_number ? ` · ${v.registration_number}` : ' (no reg. number)'}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Service */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Service Type *</label>
                  <select
                    value={form.service_type}
                    onChange={(e) => setForm({ ...form, service_type: e.target.value })}
                    className={inputCls}
                    data-testid="warranty-service-type"
                  >
                    {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Warranty Period *</label>
                  <select
                    value={form.lifetime_warranty ? 'lifetime' : String(form.warranty_years)}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm({
                        ...form,
                        lifetime_warranty: v === 'lifetime',
                        // Keep a valid year value for the API even on lifetime.
                        warranty_years: v === 'lifetime' ? form.warranty_years : Number(v),
                      });
                    }}
                    className={inputCls}
                    data-testid="warranty-years"
                  >
                    {[1, 2, 3, 5, 7, 10, 12, 15].map((y) => (
                      <option key={y} value={String(y)}>{y} year{y > 1 ? 's' : ''}</option>
                    ))}
                    <option value="lifetime">Lifetime</option>
                  </select>
                  {form.lifetime_warranty && (
                    <p className="text-xs text-teal-700 mt-1" data-testid="warranty-lifetime-hint">
                      The certificate will read “Warranty Valid Till: Lifetime” instead of a date.
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Product Used</label>
                  <input
                    value={form.product_used}
                    onChange={(e) => setForm({ ...form, product_used: e.target.value })}
                    placeholder="e.g. UniGuard Pro Gloss"
                    className={inputCls}
                    data-testid="warranty-product"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Coverage Area</label>
                  <input
                    value={form.coverage_area}
                    onChange={(e) => setForm({ ...form, coverage_area: e.target.value })}
                    placeholder="Full body / Partial / Hood + Fenders"
                    className={inputCls}
                    data-testid="warranty-coverage"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Installer</label>
                  <input
                    value={form.installer_name}
                    onChange={(e) => setForm({ ...form, installer_name: e.target.value })}
                    className={inputCls}
                    data-testid="warranty-installer"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Location</label>
                  <input
                    value={form.installer_location}
                    onChange={(e) => setForm({ ...form, installer_location: e.target.value })}
                    className={inputCls}
                    data-testid="warranty-location"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Installation Date</label>
                  <input
                    type="date"
                    value={form.installation_date}
                    onChange={(e) => setForm({ ...form, installation_date: e.target.value })}
                    className={inputCls}
                    data-testid="warranty-install-date"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Roll ID (Sunfilm only)</label>
                  <input
                    value={form.roll_id}
                    onChange={(e) => setForm({ ...form, roll_id: e.target.value })}
                    placeholder="Optional"
                    className={inputCls}
                    data-testid="warranty-roll-id"
                  />
                </div>
              </div>

              <div className="space-y-2 bg-slate-50 border border-slate-200 rounded-lg p-3">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={form.auto_approve}
                    onChange={(e) => setForm({ ...form, auto_approve: e.target.checked })}
                    data-testid="warranty-auto-approve"
                  />
                  Mark as <b>Approved</b> immediately (skip the under-review queue)
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={form.send_email}
                    onChange={(e) => setForm({ ...form, send_email: e.target.checked })}
                    disabled={!customer?.email}
                    data-testid="warranty-send-email"
                  />
                  Email the warranty ID to{' '}
                  <span className="font-medium">{customer?.email || '— no email on customer profile —'}</span>
                </label>
              </div>

              {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}

              {!canSave && selectedVehicle && !selectedVehicle.registration_number && (
                <div className="text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2.5 flex items-start gap-2" data-testid="warranty-missing-reg">
                  <span className="text-amber-600 mt-0.5">⚠</span>
                  <div>
                    <div className="font-semibold">Vehicle is missing a registration number.</div>
                    <div className="text-xs mt-0.5">
                      Open the customer{customer?.id ? <> · <a href={`/admin/billing/customers/${customer.id}`} target="_blank" rel="noopener noreferrer" className="underline font-medium">{customer?.name || 'profile'}</a></> : ''} and add the reg. number to <b>{selectedVehicle.make} {selectedVehicle.model}</b>, then come back here.
                    </div>
                  </div>
                </div>
              )}
              {!canSave && (!selectedVehicle || vehicles.length === 0) && (
                <div className="text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2.5 flex items-start gap-2">
                  <span className="text-amber-600 mt-0.5">⚠</span>
                  <div>This customer has no vehicles. Add one on the customer profile first.</div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || !canSave}
                  className="inline-flex items-center gap-1.5 bg-teal-500 hover:bg-teal-600 disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2 rounded-lg"
                  data-testid="warranty-create-btn"
                  title={!canSave ? 'Selected vehicle needs a registration number' : ''}
                >
                  {saving ? <Loader2 className="animate-spin" size={14} /> : <ShieldCheck size={14} />}
                  Create Warranty
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const SuccessView = ({ result, vehicle, customer, serviceType, onShare, onCopy, onClose }) => (
  <div className="space-y-4" data-testid="warranty-success-view">
    <div className="text-center bg-emerald-50 border border-emerald-200 rounded-lg p-5">
      <ShieldCheck size={36} className="text-emerald-600 mx-auto mb-2" />
      <div className="text-xs uppercase tracking-wider text-emerald-700">Warranty ID</div>
      <div className="font-mono text-2xl text-emerald-900 font-bold mt-1" data-testid="warranty-id-value">{result.warranty_id}</div>
      <div className="text-xs text-emerald-700 mt-2">
        Status: <span className="font-semibold">{(result.status || '').replace('_', ' ').toUpperCase()}</span>
        {' · '}Valid till {result.lifetime_warranty ? 'Lifetime' : result.warranty_expiry}
      </div>
    </div>

    <div className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-1">
      <div><span className="text-slate-500">Customer:</span> <b>{customer?.name}</b></div>
      <div><span className="text-slate-500">Vehicle:</span> {vehicle?.make} {vehicle?.model} · {vehicle?.registration_number}</div>
      <div><span className="text-slate-500">Service:</span> {serviceType}</div>
      {result.email_sent && <div className="text-emerald-700">✓ Email sent to {customer?.email}</div>}
      {(result.before_images_count > 0 || result.after_images_count > 0) && (
        <div className="text-teal-700" data-testid="warranty-photos-attached">
          📸 Attached {result.after_images_count || 0} after-install
          {result.before_images_count ? ` + ${result.before_images_count} before-install` : ''} photos from the linked inspection.
        </div>
      )}
    </div>

    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      <a
        href={`${API}/warranty/${result.warranty_id}/certificate`}
        target="_blank"
        rel="noopener noreferrer"
        download={`Warranty_${result.warranty_id}.pdf`}
        className="inline-flex items-center justify-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium px-3 py-2 rounded-lg"
        data-testid="warranty-download-cert"
      >
        <Download size={14} /> Certificate
      </a>
      <button
        onClick={onShare}
        className="inline-flex items-center justify-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium px-3 py-2 rounded-lg"
        data-testid="warranty-share-whatsapp"
      >
        <Share2 size={14} /> WhatsApp
      </button>
      <button
        onClick={onCopy}
        className="inline-flex items-center justify-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm font-medium px-3 py-2 rounded-lg border border-slate-300"
        data-testid="warranty-copy-id"
      >
        <Copy size={14} /> Copy ID
      </button>
      <a
        href={result.verify_url || 'https://unisol.global/e-warranty'}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center gap-1.5 bg-white hover:bg-slate-50 text-slate-700 text-sm font-medium px-3 py-2 rounded-lg border border-slate-300"
        data-testid="warranty-public-link"
      >
        <ExternalLink size={14} /> Public Page
      </a>
    </div>

    <div className="flex justify-end pt-2">
      <button onClick={onClose} className="text-sm text-slate-500 hover:text-slate-700">Close</button>
    </div>
  </div>
);

export default InvoiceWarrantyModal;
